<template>
  <div class="p-2 sm:p-4 text-lexend overflow-x-hidden min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <!-- HEADER -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
      <div>
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-blue-600 rounded-full"></div>
          <span class="text-[10px] font-black text-blue-600 tracking-widest">Recarga de Gas</span>
        </div>
        <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white flex items-center gap-2 flex-wrap">
          <Icon icon="mdi:clipboard-list" class="text-blue-600" />
          Pedidos de Recarga
          <span class="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-600 text-xs font-black rounded-lg">{{ pedidos.length }}</span>
          <span v-if="nuevosIds.size > 0" class="px-2 py-0.5 bg-amber-500 text-white text-xs font-black rounded-lg animate-bounce">
            +{{ nuevosIds.size }} nuevo{{ nuevosIds.size > 1 ? 's' : '' }}
          </span>
        </h1>
      </div>

      <div class="flex items-center gap-3">
        <div class="flex items-center gap-2 text-[10px] font-bold text-gray-400">
          <span class="flex items-center gap-1">
            <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            {{ haceCuanto }}
          </span>
          <button @click="sonidoActivo = !sonidoActivo" :title="sonidoActivo ? 'Silenciar' : 'Activar sonido'"
            class="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <Icon :icon="sonidoActivo ? 'mdi:volume-high' : 'mdi:volume-off'" class="text-base" />
          </button>
        </div>
        <router-link to="/gas/pedidos/nuevo"
          class="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-black text-xs tracking-widest shadow-md flex items-center gap-2">
          <Icon icon="mdi:plus-circle" /> Nuevo Pedido
        </router-link>
      </div>
    </div>

    <!-- KPIs -->
    <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 mb-4">
      <div class="bg-gradient-to-br from-amber-500 to-orange-500 text-white rounded-2xl p-3 sm:p-4 shadow-md">
        <p class="text-[9px] sm:text-[10px] font-black uppercase tracking-widest opacity-80">Pendientes</p>
        <p class="text-2xl sm:text-3xl font-black">{{ contador('PENDIENTE') }}</p>
        <p class="text-[9px] sm:text-[10px] opacity-70">Esperando pago</p>
      </div>
      <div class="bg-gradient-to-br from-rose-500 to-red-500 text-white rounded-2xl p-3 sm:p-4 shadow-md">
        <p class="text-[9px] sm:text-[10px] font-black uppercase tracking-widest opacity-80">Por verificar</p>
        <p class="text-2xl sm:text-3xl font-black">{{ contador('PAGO_PENDIENTE') }}</p>
        <p class="text-[9px] sm:text-[10px] opacity-70">Cliente reportó pago</p>
      </div>
      <div class="bg-gradient-to-br from-teal-500 to-cyan-500 text-white rounded-2xl p-3 sm:p-4 shadow-md">
        <p class="text-[9px] sm:text-[10px] font-black uppercase tracking-widest opacity-80">Pago OK</p>
        <p class="text-2xl sm:text-3xl font-black">{{ contador('PAGO_VERIFICADO') }}</p>
        <p class="text-[9px] sm:text-[10px] opacity-70">Listos para delivery</p>
      </div>
      <div class="bg-gradient-to-br from-blue-500 to-indigo-500 text-white rounded-2xl p-3 sm:p-4 shadow-md">
        <p class="text-[9px] sm:text-[10px] font-black uppercase tracking-widest opacity-80">En curso</p>
        <p class="text-2xl sm:text-3xl font-black">{{ contador('ASIGNADO') + contador('EN_CAMINO') + contador('EN_PROCESO') }}</p>
        <p class="text-[9px] sm:text-[10px] opacity-70">En reparto</p>
      </div>
      <div class="bg-gradient-to-br from-emerald-500 to-green-500 text-white rounded-2xl p-3 sm:p-4 shadow-md col-span-2 sm:col-span-3 lg:col-span-1">
        <p class="text-[9px] sm:text-[10px] font-black uppercase tracking-widest opacity-80">Completados hoy</p>
        <p class="text-2xl sm:text-3xl font-black">{{ completadosHoy }}</p>
        <p class="text-[9px] sm:text-[10px] opacity-70">${{ ingresosHoy.toFixed(0) }} cobrados</p>
      </div>
    </div>

    <!-- FILTROS -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border p-3 sm:p-4 mb-4 dark:border-gray-700">
      <div class="relative mb-3">
        <Icon icon="mdi:magnify" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
        <input v-model="searchQuery" placeholder="Buscar número, cliente..."
          class="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-blue-500 rounded-xl outline-none dark:text-white font-bold text-sm" />
      </div>

      <div class="flex flex-wrap gap-2">
        <button v-for="chip in chipsEstado" :key="chip.value" @click="filterEstado = chip.value"
          :class="[
            'px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 transition-all',
            filterEstado === chip.value
              ? 'bg-blue-600 text-white shadow-md scale-105'
              : 'bg-white dark:bg-gray-800 text-gray-500 border border-gray-200 dark:border-gray-700 hover:border-blue-400'
          ]">
          <span>{{ chip.label }}</span>
          <span :class="['px-1.5 py-0.5 rounded-full text-[9px]', filterEstado === chip.value ? 'bg-white/20' : 'bg-gray-100 dark:bg-gray-700']">
            {{ chip.count }}
          </span>
        </button>
      </div>

      <button @click="load" :disabled="loading"
        class="mt-3 w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-xl font-black text-[10px] flex items-center justify-center gap-1 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50">
        <Icon icon="mdi:refresh" :class="{ 'animate-spin': loading }" /> Actualizar
      </button>
    </div>

    <!-- ═══════════════════════════════════════════════ -->
    <!-- 📱 VISTA MÓVIL: TARJETAS -->
    <!-- ═══════════════════════════════════════════════ -->
    <div class="lg:hidden space-y-3">
      <div v-if="loading && pedidos.length === 0" v-for="i in 3" :key="'ml'+i"
        class="bg-white dark:bg-gray-800 rounded-2xl p-4 animate-pulse border dark:border-gray-700">
        <div class="h-4 bg-gray-100 dark:bg-gray-700 rounded w-1/3 mb-3"></div>
        <div class="h-3 bg-gray-100 dark:bg-gray-700 rounded w-2/3"></div>
      </div>

      <div v-if="!loading && filteredPedidosPaginados.length === 0"
        class="bg-white dark:bg-gray-800 rounded-2xl p-10 text-center border dark:border-gray-700">
        <Icon icon="mdi:clipboard-text-off" class="text-5xl mb-2 mx-auto text-gray-300" />
        <p class="text-sm font-black text-gray-400">No hay pedidos</p>
      </div>

      <div v-for="p in filteredPedidosPaginados" :key="'m-'+p.idPedidoRecarga"
        :class="[
          'bg-white dark:bg-gray-800 rounded-2xl shadow-sm border overflow-hidden transition-all',
          nuevosIds.has(p.idPedidoRecarga)
            ? 'border-amber-400 ring-2 ring-amber-300/50 animate-pulse-slow'
            : 'border-gray-100 dark:border-gray-700'
        ]">
        <div class="px-4 py-2.5 flex items-center justify-between bg-gradient-to-r from-blue-600 to-indigo-600">
          <div class="flex items-center gap-2 min-w-0">
            <Icon icon="mdi:package-variant-closed" class="text-white text-sm flex-shrink-0" />
            <span class="font-black text-white text-sm truncate">{{ p.numeroPedido }}</span>
            <span v-if="nuevosIds.has(p.idPedidoRecarga)"
              class="px-1.5 py-0.5 bg-amber-500 text-white text-[8px] font-black rounded uppercase flex-shrink-0">Nuevo</span>
          </div>
          <span :class="['px-2 py-0.5 text-[9px] font-black uppercase rounded whitespace-nowrap', getEstadoColor(p.estado)]">
            {{ labelEstado(p.estado) }}
          </span>
        </div>

        <div v-if="p.codigoEntrega" class="px-4 py-3 bg-gradient-to-r from-gray-900 to-gray-800 flex items-center justify-between gap-3">
          <div class="flex items-center gap-2 min-w-0">
            <Icon icon="mdi:lock-outline" class="text-emerald-400 text-sm flex-shrink-0" />
            <div class="min-w-0">
              <p class="text-[9px] font-black text-emerald-400 uppercase tracking-widest">Código</p>
              <p class="font-mono font-black text-lg text-emerald-400 tracking-[0.2em] leading-tight">
                {{ p.codigoEntrega }}
              </p>
            </div>
          </div>
          <button @click="copiarCodigo(p.codigoEntrega)"
            class="h-9 w-9 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center active:scale-95 transition flex-shrink-0">
            <Icon icon="mdi:content-copy" class="text-base" />
          </button>
        </div>

        <div class="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
          <div class="flex items-start gap-2">
            <div class="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
              <Icon icon="mdi:account" class="text-white text-sm" />
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-black text-gray-900 dark:text-white truncate">{{ p.nombreCliente }}</p>
              <p class="text-[11px] text-gray-500 font-bold">{{ p.telefonoCliente }}</p>
            </div>
            <a v-if="p.telefonoCliente" :href="`tel:${p.telefonoCliente}`"
              class="w-9 h-9 bg-blue-600 text-white rounded-lg flex items-center justify-center flex-shrink-0 active:scale-95 transition">
              <Icon icon="mdi:phone" class="text-sm" />
            </a>
            <a v-if="p.telefonoCliente"
              :href="`https://wa.me/${String(p.telefonoCliente).replace(/\D/g,'').replace(/^0/,'58')}`"
              target="_blank" rel="noopener"
              class="w-9 h-9 bg-emerald-600 text-white rounded-lg flex items-center justify-center flex-shrink-0 active:scale-95 transition">
              <Icon icon="mdi:whatsapp" class="text-sm" />
            </a>
          </div>
        </div>

        <div class="px-4 py-3 grid grid-cols-2 gap-3 border-b border-gray-100 dark:border-gray-700">
          <div>
            <p class="text-[9px] font-black text-gray-400 uppercase">Zona</p>
            <p v-if="p.nombreZona" class="text-xs font-bold text-gray-800 dark:text-white flex items-center gap-1 mt-0.5">
              <Icon icon="mdi:map-marker" class="text-blue-500 text-sm" />
              {{ p.nombreZona }}
            </p>
            <span v-else class="inline-block mt-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-[9px] font-black rounded uppercase">
              Sin asignar
            </span>
          </div>
          <div class="text-right">
            <p class="text-[9px] font-black text-gray-400 uppercase">Total</p>
            <p class="text-base font-black text-emerald-600 leading-tight">${{ Number(p.totalUsd || 0).toFixed(2) }}</p>
            <p class="text-[10px] text-gray-500">Bs. {{ Number(p.totalBs || 0).toFixed(2) }}</p>
          </div>
        </div>

        <div class="px-4 py-3 flex items-center justify-between gap-2 border-b border-gray-100 dark:border-gray-700">
          <span :class="['px-2 py-1 text-[10px] font-black uppercase rounded-lg whitespace-nowrap',
            p.estadoPago === 'pagado' ? 'bg-green-100 text-green-700'
            : p.estadoPago === 'parcial' ? 'bg-amber-100 text-amber-700'
            : p.estadoPago === 'verificado' ? 'bg-teal-100 text-teal-700'
            : 'bg-red-100 text-red-700']">
            {{ labelPago(p) }}
          </span>
          <span class="text-[10px] text-gray-500 font-bold">{{ formatFecha(p.fechaPedido) }}</span>
        </div>

        <div class="px-3 py-3 bg-gray-50 dark:bg-gray-900/30 flex gap-2">
          <button @click="accionPrincipal(p).handler()"
            :class="['flex-1 px-3 py-2.5 rounded-xl text-[11px] font-black uppercase flex items-center justify-center gap-1.5 text-white shadow-sm transition active:scale-95', colorBtn(accionPrincipal(p).color)]">
            <Icon :icon="accionPrincipal(p).icon" class="text-base" />
            {{ accionPrincipal(p).label }}
          </button>
          <button @click="toggleMenuAcciones(p.idPedidoRecarga)"
            class="w-11 h-11 bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl flex items-center justify-center active:scale-95 transition flex-shrink-0">
            <Icon icon="mdi:dots-vertical" class="text-base text-gray-600 dark:text-gray-300" />
          </button>
        </div>
      </div>

      <!-- ⭐ PAGINACIÓN MÓVIL -->
      <div v-if="filteredPedidos.length > 0" class="flex items-center justify-center gap-1 pt-4 flex-wrap">
        <button @click="irAPagina(1)" :disabled="paginaActual === 1"
          class="h-9 w-9 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center active:scale-95 transition">
          <Icon icon="mdi:chevron-double-left" class="text-base text-gray-600 dark:text-gray-300" />
        </button>
        <button @click="irAPagina(paginaActual - 1)" :disabled="paginaActual === 1"
          class="h-9 w-9 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center active:scale-95 transition">
          <Icon icon="mdi:chevron-left" class="text-base text-gray-600 dark:text-gray-300" />
        </button>

        <button v-for="(pag, idx) in paginasVisibles" :key="'mp-'+idx"
          @click="typeof pag === 'number' && irAPagina(pag)"
          :disabled="pag === '...'"
          :class="[
            'h-9 min-w-[36px] px-2 rounded-lg font-black text-sm transition-all',
            pag === paginaActual
              ? 'bg-indigo-600 text-white shadow-md scale-105'
              : pag === '...'
                ? 'bg-transparent text-gray-400 cursor-default'
                : 'border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:border-indigo-400 active:scale-95'
          ]">
          {{ pag }}
        </button>

        <button @click="irAPagina(paginaActual + 1)" :disabled="paginaActual === totalPaginas"
          class="h-9 w-9 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center active:scale-95 transition">
          <Icon icon="mdi:chevron-right" class="text-base text-gray-600 dark:text-gray-300" />
        </button>
        <button @click="irAPagina(totalPaginas)" :disabled="paginaActual === totalPaginas"
          class="h-9 w-9 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center active:scale-95 transition">
          <Icon icon="mdi:chevron-double-right" class="text-base text-gray-600 dark:text-gray-300" />
        </button>
      </div>

      <div v-if="filteredPedidos.length > 0" class="text-center text-[10px] text-gray-400 py-2 font-bold">
        Mostrando {{ (paginaActual - 1) * itemsPorPagina + 1 }}–{{ Math.min(paginaActual * itemsPorPagina, filteredPedidos.length) }} de {{ filteredPedidos.length }}
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════ -->
    <!-- 💻 VISTA ESCRITORIO: TABLA -->
    <!-- ═══════════════════════════════════════════════ -->
    <div class="hidden lg:block bg-white dark:bg-gray-800 rounded-2xl shadow-md border dark:border-gray-700 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full text-left">
          <thead class="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
            <tr>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">N° PEDIDO</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">CÓDIGO</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">CLIENTE</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">ZONA</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-right">TOTAL</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">ESTADO</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">PAGO</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">FECHA</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-right">ACCIONES</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <tr v-if="loading && pedidos.length === 0" v-for="i in 3" :key="'l'+i" class="animate-pulse">
              <td colspan="9" class="px-5 py-4"><div class="h-4 bg-gray-100 dark:bg-gray-700 rounded"></div></td>
            </tr>
            <tr v-if="!loading && filteredPedidosPaginados.length === 0">
              <td colspan="9" class="px-5 py-16 text-center opacity-40">
                <Icon icon="mdi:clipboard-text-off" class="text-6xl mb-3 mx-auto" />
                <p class="text-lg font-black">No hay pedidos</p>
              </td>
            </tr>
            <tr v-for="p in filteredPedidosPaginados" :key="p.idPedidoRecarga"
              :class="[
                'transition-all',
                nuevosIds.has(p.idPedidoRecarga)
                  ? 'bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 animate-pulse-slow'
                  : 'hover:bg-blue-50/30 dark:hover:bg-gray-700/30'
              ]">
              <td class="px-5 py-4">
                <p class="font-black text-xs text-gray-900 dark:text-white flex items-center gap-2">
                  {{ p.numeroPedido }}
                  <span v-if="nuevosIds.has(p.idPedidoRecarga)"
                    class="px-1.5 py-0.5 bg-amber-500 text-white text-[8px] font-black rounded uppercase">Nuevo</span>
                </p>
                <p class="text-[10px] text-gray-500">{{ p.detalles?.length || 0 }} item(s)</p>
              </td>

              <td class="px-5 py-4 text-center">
                <div v-if="p.codigoEntrega" class="flex flex-col items-center gap-1">
                  <span class="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-emerald-100 to-green-100 dark:from-emerald-900/40 dark:to-green-900/40 border-2 border-emerald-300 dark:border-emerald-700 rounded-lg">
                    <Icon icon="mdi:lock-outline" class="text-emerald-600 text-xs" />
                    <span class="font-mono font-black text-sm text-emerald-700 dark:text-emerald-300 tracking-wider">
                      {{ p.codigoEntrega }}
                    </span>
                  </span>
                  <button @click="copiarCodigo(p.codigoEntrega)"
                    class="text-[9px] font-black text-emerald-600 hover:text-emerald-700 flex items-center gap-0.5 transition">
                    <Icon icon="mdi:content-copy" class="text-xs" /> Copiar
                  </button>
                </div>
                <span v-else class="text-[10px] text-gray-400 font-bold italic">—</span>
              </td>

              <td class="px-5 py-4">
                <p class="font-bold text-xs text-gray-800 dark:text-white">{{ p.nombreCliente }}</p>
                <p class="text-[10px] text-gray-500">{{ p.telefonoCliente }}</p>
              </td>
              <td class="px-5 py-4 text-xs font-bold dark:text-gray-200">
                <span v-if="p.nombreZona" class="flex items-center gap-1 flex-wrap">
                  <Icon icon="mdi:map-marker" class="text-blue-500 text-sm" />
                  {{ p.nombreZona }}
                  <span v-if="p.estado === 'PENDIENTE'"
                    class="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[8px] font-black rounded uppercase">
                    Esperando pago
                  </span>
                </span>
                <span v-else class="px-2 py-0.5 bg-amber-100 text-amber-700 text-[9px] font-black rounded uppercase">
                  Sin asignar
                </span>
              </td>
              <td class="px-5 py-4 text-right">
                <p class="font-black text-sm text-emerald-600">${{ Number(p.totalUsd || 0).toFixed(2) }}</p>
                <p class="text-[10px] text-gray-500">Bs. {{ Number(p.totalBs || 0).toFixed(2) }}</p>
              </td>
              <td class="px-5 py-4 text-center">
                <span :class="['px-2 py-1 text-[10px] font-black uppercase rounded-lg whitespace-nowrap', getEstadoColor(p.estado)]">
                  {{ labelEstado(p.estado) }}
                </span>
              </td>
              <td class="px-5 py-4 text-center">
                <span :class="['px-2 py-1 text-[10px] font-black uppercase rounded-lg whitespace-nowrap',
                  p.estadoPago === 'pagado' ? 'bg-green-100 text-green-700'
                  : p.estadoPago === 'parcial' ? 'bg-amber-100 text-amber-700'
                  : p.estadoPago === 'verificado' ? 'bg-teal-100 text-teal-700'
                  : 'bg-red-100 text-red-700']">
                  {{ labelPago(p) }}
                </span>
              </td>
              <td class="px-5 py-4 text-xs text-gray-600 dark:text-gray-300">{{ formatFecha(p.fechaPedido) }}</td>
              <td class="px-5 py-4 text-right">
                <div class="flex items-center justify-end gap-1">
                  <button @click="accionPrincipal(p).handler()"
                    :class="['px-3 py-1.5 rounded-lg text-[10px] font-black uppercase flex items-center gap-1 text-white shadow-sm transition-all hover:scale-105', colorBtn(accionPrincipal(p).color)]">
                    <Icon :icon="accionPrincipal(p).icon" class="text-sm" />
                    {{ accionPrincipal(p).label }}
                  </button>

                  <div class="relative">
                    <button @click.stop="toggleMenuAcciones(p.idPedidoRecarga)"
                      class="p-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                      <Icon icon="mdi:dots-vertical" class="text-sm text-gray-600 dark:text-gray-300" />
                    </button>
                    <div v-if="menuAbierto === p.idPedidoRecarga"
                      class="absolute right-0 top-full mt-1 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border dark:border-gray-700 py-1 z-50 min-w-[200px]">
                      <button v-if="p.codigoEntrega" @click="ejecutarAccion(p, 'copiarCodigo')"
                        class="w-full px-3 py-2 text-left text-[11px] font-bold flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white">
                        <Icon icon="mdi:content-copy" class="text-emerald-600" /> Copiar código
                      </button>
                      <button @click="ejecutarAccion(p, 'zona')" :disabled="!puedeAsignarZona(p)"
                        class="w-full px-3 py-2 text-left text-[11px] font-bold flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white disabled:opacity-40 disabled:cursor-not-allowed">
                        <Icon icon="mdi:map-marker-plus" class="text-blue-600" /> Asignar zona
                      </button>
                      <button @click="ejecutarAccion(p, 'estado')"
                        class="w-full px-3 py-2 text-left text-[11px] font-bold flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white">
                        <Icon icon="mdi:swap-horizontal" class="text-indigo-600" /> Cambiar estado
                      </button>
                      <button @click="ejecutarAccion(p, 'pago')" :disabled="!puedeRegistrarPago(p)"
                        class="w-full px-3 py-2 text-left text-[11px] font-bold flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white disabled:opacity-40 disabled:cursor-not-allowed">
                        <Icon icon="mdi:cash-register" class="text-emerald-600" /> Registrar pago
                      </button>
                      <button @click="ejecutarAccion(p, 'verificarPago')" :disabled="!puedeVerificarPago(p)"
                        class="w-full px-3 py-2 text-left text-[11px] font-bold flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white disabled:opacity-40 disabled:cursor-not-allowed">
                        <Icon icon="mdi:check-decagram" class="text-teal-600" /> Verificar pago
                      </button>
                      <button @click="ejecutarAccion(p, 'delivery')" :disabled="!puedeAsignarDelivery(p)"
                        class="w-full px-3 py-2 text-left text-[11px] font-bold flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white disabled:opacity-40 disabled:cursor-not-allowed">
                        <Icon icon="mdi:moped" class="text-purple-600" /> Asignar delivery
                      </button>
                      <button @click="ejecutarAccion(p, 'detalle')"
                        class="w-full px-3 py-2 text-left text-[11px] font-bold flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white">
                        <Icon icon="mdi:eye" class="text-indigo-600" /> Ver detalle
                      </button>
                      <button @click="ejecutarAccion(p, 'imprimirTicket')"
                        class="w-full px-3 py-2 text-left text-[11px] font-bold flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white">
                        <Icon icon="mdi:printer" class="text-slate-600" /> Imprimir ticket
                      </button>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ⭐ PAGINACIÓN ESCRITORIO -->
      <div v-if="filteredPedidos.length > 0"
        class="px-5 py-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div class="flex items-center gap-2">
          <span class="text-[10px] font-black text-gray-400 uppercase tracking-wider">Mostrar:</span>
          <button v-for="n in [10, 20, 50, 100]" :key="n"
            @click="itemsPorPagina = n; paginaActual = 1"
            :class="[
              'px-3 py-1 text-[11px] font-black rounded-lg transition-all',
              itemsPorPagina === n
                ? 'bg-blue-600 text-white shadow-sm'
                : 'bg-white dark:bg-gray-800 text-gray-500 border border-gray-200 dark:border-gray-700 hover:border-blue-400'
            ]">
            {{ n }}
          </button>
        </div>

        <div class="flex items-center gap-1 flex-wrap justify-center">
          <button @click="irAPagina(1)" :disabled="paginaActual === 1"
            class="h-9 w-9 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center hover:border-indigo-400 active:scale-95 transition">
            <Icon icon="mdi:chevron-double-left" class="text-base text-gray-600 dark:text-gray-300" />
          </button>

          <button @click="irAPagina(paginaActual - 1)" :disabled="paginaActual === 1"
            class="h-9 w-9 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center hover:border-indigo-400 active:scale-95 transition">
            <Icon icon="mdi:chevron-left" class="text-base text-gray-600 dark:text-gray-300" />
          </button>

          <button v-for="(pag, idx) in paginasVisibles" :key="'dp-'+idx"
            @click="typeof pag === 'number' && irAPagina(pag)"
            :disabled="pag === '...'"
            :class="[
              'h-9 min-w-[36px] px-2 rounded-lg font-black text-sm transition-all',
              pag === paginaActual
                ? 'bg-indigo-600 text-white shadow-md scale-105'
                : pag === '...'
                  ? 'bg-transparent text-gray-400 cursor-default'
                  : 'border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:border-indigo-400 active:scale-95'
            ]">
            {{ pag }}
          </button>

          <button @click="irAPagina(paginaActual + 1)" :disabled="paginaActual === totalPaginas"
            class="h-9 w-9 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center hover:border-indigo-400 active:scale-95 transition">
            <Icon icon="mdi:chevron-right" class="text-base text-gray-600 dark:text-gray-300" />
          </button>

          <button @click="irAPagina(totalPaginas)" :disabled="paginaActual === totalPaginas"
            class="h-9 w-9 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center hover:border-indigo-400 active:scale-95 transition">
            <Icon icon="mdi:chevron-double-right" class="text-base text-gray-600 dark:text-gray-300" />
          </button>
        </div>

        <span class="text-[11px] font-bold text-gray-500">
          {{ (paginaActual - 1) * itemsPorPagina + 1 }}–{{ Math.min(paginaActual * itemsPorPagina, filteredPedidos.length) }} de {{ filteredPedidos.length }}
        </span>
      </div>
    </div>

    <div v-if="menuAbierto" class="fixed inset-0 z-40" @click="menuAbierto = null"></div>

    <!-- ==================== MODAL CONFIRMAR / ASIGNAR ZONA / CAMBIAR ESTADO ==================== -->
    <Teleport to="body">
      <div v-if="modalEstado" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 z-[9998]" @click.self="cerrarModalEstado">
        <div class="bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[95vh] overflow-hidden">
          <div class="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center justify-between sm:rounded-t-2xl">
            <div>
              <h3 class="text-sm font-black text-white">
                <template v-if="requiereZona">Confirmar Pedido y Asignar Zona</template>
                <template v-else>Cambiar Estado del Pedido</template>
              </h3>
              <p v-if="requiereZona" class="text-[10px] text-white/70 font-bold mt-0.5">
                Debes asignar una zona para confirmar
              </p>
            </div>
            <button @click="cerrarModalEstado" class="p-1 bg-white/10 rounded text-white"><Icon icon="mdi:close" /></button>
          </div>
          <div class="p-6 space-y-4 overflow-y-auto max-h-[calc(95vh-80px)]">

            <!-- ⭐ INFO COMPLETA DEL CLIENTE + MAPA + UBICACIÓN -->
            <div v-if="pedidoSeleccionado" class="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-4 border-2 border-blue-200 dark:border-blue-800 space-y-3">

              <!-- Pedido + cliente -->
              <div class="flex items-start justify-between gap-2">
                <div class="min-w-0">
                  <p class="text-[9px] font-black text-blue-500 uppercase tracking-widest">Pedido</p>
                  <p class="font-black text-blue-800 dark:text-blue-200 text-sm truncate">{{ pedidoSeleccionado.numeroPedido }}</p>
                </div>
                <div class="text-right min-w-0">
                  <p class="text-[9px] font-black text-blue-500 uppercase tracking-widest">Cliente</p>
                  <p class="font-black text-gray-900 dark:text-white text-sm truncate">{{ pedidoSeleccionado.nombreCliente }}</p>
                </div>
              </div>

              <!-- Teléfono (clickeable) -->
              <div v-if="pedidoSeleccionado.telefonoCliente" class="flex items-center gap-2">
                <Icon icon="mdi:phone" class="text-blue-600 text-sm flex-shrink-0" />
                <a :href="`tel:${pedidoSeleccionado.telefonoCliente}`"
                  class="text-xs font-bold text-blue-700 dark:text-blue-300 hover:underline">
                  {{ pedidoSeleccionado.telefonoCliente }}
                </a>
                <a :href="`https://wa.me/${String(pedidoSeleccionado.telefonoCliente).replace(/\D/g,'').replace(/^0/,'58')}`"
                  target="_blank" rel="noopener"
                  class="ml-auto h-7 w-7 rounded-lg bg-emerald-600 text-white flex items-center justify-center active:scale-95 transition">
                  <Icon icon="mdi:whatsapp" class="text-sm" />
                </a>
              </div>

              <!-- Dirección -->
              <div v-if="pedidoSeleccionado.direccionEntrega" class="flex items-start gap-2">
                <Icon icon="mdi:map-marker" class="text-rose-600 text-sm flex-shrink-0 mt-0.5" />
                <div class="min-w-0">
                  <p class="text-[9px] font-black text-rose-500 uppercase tracking-widest">Dirección de entrega</p>
                  <p class="text-xs font-bold text-gray-800 dark:text-white leading-snug">
                    {{ pedidoSeleccionado.direccionEntrega }}
                  </p>
                </div>
              </div>

              <!-- Punto de referencia -->
              <div v-if="pedidoSeleccionado.puntoReferencia" class="flex items-start gap-2">
                <Icon icon="mdi:information" class="text-amber-600 text-sm flex-shrink-0 mt-0.5" />
                <div class="min-w-0">
                  <p class="text-[9px] font-black text-amber-600 uppercase tracking-widest">Referencia</p>
                  <p class="text-xs font-bold text-gray-700 dark:text-gray-300 leading-snug">
                    {{ pedidoSeleccionado.puntoReferencia }}
                  </p>
                </div>
              </div>

              <!-- ⭐ MAPA embebido + link clickeable -->
              <div v-if="pedidoSeleccionado.urlMapaEntrega" class="space-y-2">
                <p class="text-[9px] font-black text-indigo-500 uppercase tracking-widest flex items-center gap-1">
                  <Icon icon="mdi:map" class="text-sm" /> Ubicación en el mapa
                </p>

                <div v-if="mapaEmbedUrl(pedidoSeleccionado)"
                  class="rounded-xl overflow-hidden border-2 border-indigo-200 dark:border-indigo-800 shadow-sm bg-gray-100 dark:bg-gray-800">
                  <iframe
                    :src="mapaEmbedUrl(pedidoSeleccionado)"
                    width="100%"
                    height="180"
                    style="border:0;"
                    loading="lazy"
                    referrerpolicy="no-referrer-when-downgrade"
                    allowfullscreen>
                  </iframe>
                </div>

                <a :href="pedidoSeleccionado.urlMapaEntrega"
                  target="_blank"
                  rel="noopener"
                  class="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-xl font-black text-xs uppercase shadow-sm active:scale-95 transition">
                  <Icon icon="mdi:google-maps" class="text-base" />
                  Abrir en Google Maps
                </a>
              </div>

              <!-- Si NO hay mapa -->
              <div v-else class="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300 rounded-xl p-3 flex items-start gap-2">
                <Icon icon="mdi:alert-circle" class="text-amber-600 text-base flex-shrink-0 mt-0.5" />
                <p class="text-[10px] font-bold text-amber-800 dark:text-amber-200 leading-snug">
                  Este cliente no tiene ubicación en el mapa. Pídele el link o usa solo la dirección escrita.
                </p>
              </div>
            </div>

            <div>
              <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                Nuevo Estado <span class="text-rose-500">*</span>
              </label>
              <select v-model="nuevoEstado" :disabled="requiereZona"
                class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 rounded-xl text-xs font-bold dark:text-white disabled:opacity-70">
                <option value="PENDIENTE">PENDIENTE</option>
                <option value="CONFIRMADO">CONFIRMADO</option>
                <option value="PAGO_PENDIENTE">PAGO PENDIENTE (cliente debe pagar)</option>
                <option value="PAGO_VERIFICADO">PAGO VERIFICADO</option>
                <option value="ASIGNADO">ASIGNADO</option>
                <option value="EN_CAMINO">EN CAMINO</option>
                <option value="EN_PROCESO">EN PROCESO</option>
                <option value="COMPLETADO">COMPLETADO</option>
                <option value="CANCELADO">CANCELADO</option>
              </select>
            </div>

            <div v-if="requiereZona" class="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300 rounded-xl p-4 space-y-3">
              <div class="flex items-start gap-2">
                <Icon icon="mdi:alert-circle" class="text-amber-600 text-lg flex-shrink-0 mt-0.5" />
                <p class="text-[10px] font-bold text-amber-800 dark:text-amber-200 leading-relaxed">
                  Para confirmar este pedido es <strong>obligatorio asignar una zona</strong>. La zona define qué delivery podrá atenderlo.
                </p>
              </div>

              <div>
                <label class="block text-[10px] font-black text-amber-800 dark:text-amber-200 tracking-widest mb-2">
                  Zona de entrega <span class="text-rose-500">*</span>
                </label>
                <select v-model="zonaSeleccionada"
                  class="w-full px-4 py-3 bg-white dark:bg-gray-900/50 border-2 border-amber-300 focus:border-amber-500 rounded-xl text-xs font-bold dark:text-white outline-none">
                  <option :value="null">-- Seleccione una zona --</option>
                  <option v-for="z in zonasDisponibles" :key="z.idZona" :value="z.idZona">
                    {{ z.nombre }} <span v-if="z.tarifaUsd"> — ${{ z.tarifaUsd }}</span>
                  </option>
                </select>
                <p v-if="zonasDisponibles.length === 0" class="text-[10px] text-rose-600 font-bold mt-2">
                  ⚠ No hay zonas configuradas. Contacta al administrador.
                </p>
              </div>
            </div>

            <div v-else-if="pedidoSeleccionado?.nombreZona"
              class="bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-xl p-4">
              <div class="flex items-start gap-2">
                <Icon icon="mdi:map-marker" class="text-blue-600 text-lg flex-shrink-0 mt-0.5" />
                <div class="flex-1">
                  <p class="text-[10px] font-black text-blue-700 dark:text-blue-300 uppercase tracking-widest">
                    Zona del pedido
                  </p>
                  <p class="text-sm font-black text-blue-800 dark:text-blue-200">
                    {{ pedidoSeleccionado.nombreZona }}
                  </p>
                  <p class="text-[10px] text-blue-600 dark:text-blue-400 mt-1 leading-relaxed">
                    Heredada del cliente. Si necesitas cambiarla, usa
                    <strong>Acciones → Asignar zona</strong>.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Observaciones</label>
              <textarea v-model="obsEstado" rows="2" class="w-full px-4 py-2 border-2 rounded-xl text-xs dark:bg-gray-900/50 dark:text-white"></textarea>
            </div>

            <div class="flex gap-3 pt-4">
              <button @click="cerrarModalEstado" class="flex-1 py-3 border-2 rounded-xl font-black text-xs">Cancelar</button>
              <button @click="cambiarEstado" :disabled="saving || (requiereZona && !zonaSeleccionada)"
                class="flex-[2] py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-black text-xs uppercase disabled:opacity-50 disabled:cursor-not-allowed">
                {{ saving ? 'Procesando...' : (requiereZona ? 'Confirmar y Asignar Zona' : 'Cambiar Estado') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ==================== MODAL ASIGNAR ZONA ==================== -->
    <Teleport to="body">
      <div v-if="modalZona" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 z-[9998]" @click.self="modalZona = false">
        <div class="bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md">
          <div class="bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-4 flex items-center justify-between sm:rounded-t-2xl">
            <h3 class="text-sm font-black text-white">Asignar Zona al Pedido</h3>
            <button @click="modalZona = false" class="p-1 bg-white/10 rounded text-white"><Icon icon="mdi:close" /></button>
          </div>
          <div class="p-6 space-y-4">
            <div v-if="pedidoSeleccionado" class="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3 text-[10px]">
              <p class="font-black text-blue-700 dark:text-blue-300">Pedido {{ pedidoSeleccionado.numeroPedido }}</p>
              <p class="text-gray-600 dark:text-gray-400">{{ pedidoSeleccionado.nombreCliente }}</p>
              <p v-if="pedidoSeleccionado.nombreZona" class="text-blue-600 font-bold mt-1">
                Zona actual: {{ pedidoSeleccionado.nombreZona }}
              </p>
            </div>

            <div>
              <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                Zona <span class="text-rose-500">*</span>
              </label>
              <select v-model="zonaSeleccionada"
                class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 rounded-xl text-xs font-bold dark:text-white">
                <option :value="null">-- Seleccione una zona --</option>
                <option v-for="z in zonasDisponibles" :key="z.idZona" :value="z.idZona">
                  {{ z.nombre }} <span v-if="z.tarifaUsd"> — ${{ z.tarifaUsd }}</span>
                </option>
              </select>
            </div>

            <div>
              <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Observaciones</label>
              <textarea v-model="obsZona" rows="2" class="w-full px-4 py-2 border-2 rounded-xl text-xs dark:bg-gray-900/50 dark:text-white"></textarea>
            </div>

            <div class="flex gap-3 pt-4">
              <button @click="modalZona = false" class="flex-1 py-3 border-2 rounded-xl font-black text-xs">Cancelar</button>
              <button @click="asignarZona" :disabled="saving || !zonaSeleccionada"
                class="flex-[2] py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-black text-xs uppercase disabled:opacity-50">
                {{ saving ? 'Guardando...' : 'Asignar Zona' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ==================== MODAL VERIFICAR PAGO ==================== -->
    <Teleport to="body">
      <div v-if="modalVerificarPago" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 z-[9998]" @click.self="modalVerificarPago = false">
        <div class="bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg max-h-[95vh] overflow-hidden">
          <div class="bg-gradient-to-r from-teal-600 to-cyan-600 px-6 py-4 flex items-center justify-between sm:rounded-t-2xl">
            <div>
              <h3 class="text-sm font-black text-white">Verificar Pago del Cliente</h3>
              <p class="text-[10px] text-white/70 font-bold mt-0.5">Confirma que el pago fue recibido</p>
            </div>
            <button @click="modalVerificarPago = false" class="p-1 bg-white/10 rounded text-white"><Icon icon="mdi:close" /></button>
          </div>
          <div class="p-6 space-y-4 overflow-y-auto max-h-[calc(95vh-80px)]">

            <div v-if="pedidoSeleccionado && pedidoSeleccionado.codigoEntrega"
              class="bg-gradient-to-r from-indigo-900 to-purple-900 rounded-2xl p-4 border-2 border-indigo-500/40">
              <div class="flex items-center justify-between gap-3">
                <div class="flex-1 min-w-0">
                  <p class="text-[10px] font-black text-indigo-300 uppercase tracking-widest flex items-center gap-1 mb-1">
                    <Icon icon="mdi:phone-in-talk" class="text-sm" />
                    Código para dictar al cliente
                  </p>
                  <p class="text-[9px] text-indigo-200/70 font-bold">
                    Si el cliente lo olvidó, dictaselo por teléfono
                  </p>
                </div>
                <div class="flex items-center gap-2 flex-shrink-0">
                  <span class="font-mono text-3xl font-black text-white tracking-[0.25em]"
                        style="text-shadow: 0 0 15px rgba(255,255,255,0.3);">
                    {{ pedidoSeleccionado.codigoEntrega }}
                  </span>
                  <button @click="copiarCodigo(pedidoSeleccionado.codigoEntrega)"
                    class="h-9 w-9 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center active:scale-95 transition">
                    <Icon icon="mdi:content-copy" class="text-base" />
                  </button>
                </div>
              </div>
            </div>

            <div v-if="pedidoSeleccionado" class="bg-teal-50 dark:bg-teal-900/20 rounded-xl p-4">
              <div class="flex justify-between text-xs mb-1">
                <span class="font-bold text-gray-500">Pedido:</span>
                <span class="font-black text-gray-900 dark:text-white">{{ pedidoSeleccionado.numeroPedido }}</span>
              </div>
              <div class="flex justify-between text-xs mb-1">
                <span class="font-bold text-gray-500">Cliente:</span>
                <span class="font-black text-gray-900 dark:text-white">{{ pedidoSeleccionado.nombreCliente }}</span>
              </div>
              <div class="flex justify-between text-xs mb-1">
                <span class="font-bold text-gray-500">Total pedido:</span>
                <span class="font-black text-gray-900 dark:text-white">${{ Number(pedidoSeleccionado.totalUsd || 0).toFixed(2) }}</span>
              </div>
              <div class="flex justify-between text-xs">
                <span class="font-bold text-gray-500">Monto reportado:</span>
                <span class="font-black text-teal-600">
                  ${{ Number(pedidoSeleccionado.montoReportadoUsd || 0).toFixed(2) }}
                </span>
              </div>
              <div v-if="pedidoSeleccionado.referenciaPagoCliente" class="mt-2 pt-2 border-t border-teal-200 dark:border-teal-800">
                <p class="text-[10px] font-black text-teal-600 uppercase mb-1">Referencia:</p>
                <p class="text-xs font-bold text-gray-700 dark:text-gray-300 font-mono break-all">
                  {{ pedidoSeleccionado.referenciaPagoCliente }}
                </p>
              </div>
              <a v-if="pedidoSeleccionado.comprobanteUrl" :href="pedidoSeleccionado.comprobanteUrl" target="_blank"
                class="mt-3 inline-flex items-center gap-1 px-3 py-2 bg-white dark:bg-gray-800 border-2 border-teal-300 rounded-lg text-[10px] font-black text-teal-700 hover:bg-teal-50 transition-colors">
                <Icon icon="mdi:file-image" class="text-base" /> Ver comprobante
              </a>
            </div>

            <div class="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300 rounded-xl p-3">
              <p class="text-[10px] font-bold text-amber-800 dark:text-amber-200 leading-relaxed flex items-start gap-2">
                <Icon icon="mdi:alert-circle" class="text-base flex-shrink-0 mt-0.5" />
                <span>Verifica que el pago realmente ingresó <strong>antes</strong> de confirmar.</span>
              </p>
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Monto USD *</label>
                <input v-model.number="pagoVerificacion.montoUsd" type="number" step="0.01"
                  class="w-full px-4 py-2 border-2 rounded-xl text-xs dark:bg-gray-900/50 dark:text-white" />
              </div>
              <div>
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Tasa Bs/USD</label>
                <input v-model.number="pagoVerificacion.tasaBsUsd" type="number" step="0.01"
                  class="w-full px-4 py-2 border-2 rounded-xl text-xs dark:bg-gray-900/50 dark:text-white" />
              </div>
            </div>

            <div class="flex flex-col sm:flex-row gap-2 pt-4">
              <button @click="modalVerificarPago = false" class="flex-1 py-3 border-2 rounded-xl font-black text-xs">Cancelar</button>
              <button @click="rechazarPago" :disabled="saving"
                class="flex-1 py-3 bg-rose-500 hover:bg-rose-600 text-white rounded-xl font-black text-xs uppercase disabled:opacity-50">
                Rechazar
              </button>
              <button @click="verificarPago" :disabled="saving || !montoVerificacionValido"
                class="flex-[2] py-3 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-xl font-black text-xs uppercase disabled:opacity-50">
                <Icon v-if="!saving" icon="mdi:check-decagram" class="inline mr-1" />
                {{ saving ? 'Verificando...' : 'Confirmar' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ==================== MODAL ASIGNAR DELIVERY ==================== -->
    <Teleport to="body">
      <div v-if="modalAsignar" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 z-[9998]" @click.self="modalAsignar = false">
        <div class="bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md">
          <div class="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4 flex items-center justify-between sm:rounded-t-2xl">
            <div>
              <h3 class="text-sm font-black text-white">Asignar Delivery</h3>
              <p class="text-[10px] text-white/70 font-bold mt-0.5">Pedido verificado y listo</p>
            </div>
            <button @click="modalAsignar = false" class="p-1 bg-white/10 rounded text-white"><Icon icon="mdi:close" /></button>
          </div>
          <div class="p-6 space-y-4">
            <div v-if="pedidoSeleccionado" class="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-3 text-[10px] space-y-1">
              <p class="font-black text-purple-700 dark:text-purple-300">{{ pedidoSeleccionado.numeroPedido }}</p>
              <p class="text-gray-600 dark:text-gray-400">{{ pedidoSeleccionado.nombreCliente }}</p>
              <p class="text-purple-600 font-bold flex items-center gap-1">
                <Icon icon="mdi:map-marker" class="text-sm" />
                Zona: {{ pedidoSeleccionado.nombreZona || 'Sin zona' }}
              </p>
            </div>

            <div>
              <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                Repartidor <span class="text-rose-500">*</span>
              </label>
              <select v-model="deliverySeleccionado" class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 rounded-xl text-xs font-bold dark:text-white">
                <option :value="null">-- Seleccione un delivery --</option>
                <option v-for="d in deliveriesDisponibles" :key="d.idDelivery" :value="d.idDelivery">
                  {{ d.nombre }} {{ d.apellido }} — {{ d.placaVehiculo || 'Sin placa' }}
                </option>
              </select>
              <p v-if="deliveriesDisponibles.length === 0" class="text-[10px] text-rose-600 font-bold mt-2">
                ⚠ No hay deliverys disponibles
              </p>
            </div>

            <div>
              <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Observaciones</label>
              <textarea v-model="obsAsignar" rows="2" class="w-full px-4 py-2 border-2 rounded-xl text-xs dark:bg-gray-900/50 dark:text-white"></textarea>
            </div>

            <div class="flex gap-3 pt-4">
              <button @click="modalAsignar = false" class="flex-1 py-3 border-2 rounded-xl font-black text-xs">Cancelar</button>
              <button @click="asignarDelivery" :disabled="saving || !deliverySeleccionado"
                class="flex-[2] py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-black text-xs uppercase disabled:opacity-50">
                {{ saving ? 'Asignando...' : 'Asignar' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ==================== MODAL PAGO MANUAL ==================== -->
    <Teleport to="body">
      <div v-if="modalPago" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 z-[9998]" @click.self="modalPago = false">
        <div class="bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md">
          <div class="bg-gradient-to-r from-emerald-600 to-green-600 px-6 py-4 flex items-center justify-between sm:rounded-t-2xl">
            <h3 class="text-sm font-black text-white">Registrar Pago</h3>
            <button @click="modalPago = false" class="p-1 bg-white/10 rounded text-white"><Icon icon="mdi:close" /></button>
          </div>
          <div class="p-6 space-y-4">
            <div v-if="pedidoSeleccionado" class="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-4">
              <div class="flex justify-between text-xs mb-1">
                <span class="font-bold text-gray-500">Total:</span>
                <span class="font-black text-gray-900 dark:text-white">${{ Number(pedidoSeleccionado.totalUsd || 0).toFixed(2) }}</span>
              </div>
              <div class="flex justify-between text-xs">
                <span class="font-bold text-gray-500">Saldo pendiente:</span>
                <span class="font-black text-red-600">${{ Number(pedidoSeleccionado.saldoPendienteUsd || 0).toFixed(2) }}</span>
              </div>
            </div>
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Monto USD *</label>
                <input v-model.number="pago.montoUsd" type="number" step="0.01" class="w-full px-4 py-2 border-2 rounded-xl text-xs dark:bg-gray-900/50 dark:text-white" />
              </div>
              <div>
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Monto Bs *</label>
                <input v-model.number="pago.montoBs" type="number" step="0.01" class="w-full px-4 py-2 border-2 rounded-xl text-xs dark:bg-gray-900/50 dark:text-white" />
              </div>
            </div>
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Tasa *</label>
                <input v-model.number="pago.tasaBsUsd" type="number" step="0.01" class="w-full px-4 py-2 border-2 rounded-xl text-xs dark:bg-gray-900/50 dark:text-white" />
              </div>
              <div>
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Método</label>
                <select v-model="pago.metodoPago" class="w-full px-4 py-2 border-2 rounded-xl text-xs dark:bg-gray-900/50 dark:text-white">
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia</option>
                  <option value="pago_movil">Pago Móvil</option>
                  <option value="zelle">Zelle</option>
                  <option value="usdt">USDT</option>
                </select>
              </div>
            </div>
            <div>
              <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Referencia</label>
              <input v-model="pago.referencia" class="w-full px-4 py-2 border-2 rounded-xl text-xs dark:bg-gray-900/50 dark:text-white" />
            </div>
            <div class="flex gap-3 pt-4">
              <button @click="modalPago = false" class="flex-1 py-3 border-2 rounded-xl font-black text-xs">Cancelar</button>
              <button @click="registrarPago" :disabled="saving"
                class="flex-[2] py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl font-black text-xs uppercase disabled:opacity-50">
                {{ saving ? 'Registrando...' : 'Registrar' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ==================== MODAL DETALLE ==================== -->
    <Teleport to="body">
      <div v-if="modalDetalle && pedidoSeleccionado" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 z-[9997]" @click.self="modalDetalle = false">
        <div class="bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-2xl shadow-2xl w-full sm:max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden">
          <div class="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex items-center justify-between sm:rounded-t-2xl">
            <h3 class="text-sm font-black text-white">Pedido {{ pedidoSeleccionado.numeroPedido }}</h3>
            <button @click="modalDetalle = false" class="p-1 bg-white/10 rounded text-white"><Icon icon="mdi:close" /></button>
          </div>
          <div class="p-6 overflow-y-auto max-h-[calc(95vh-80px)] space-y-4">

            <div v-if="pedidoSeleccionado.codigoEntrega"
              class="bg-gradient-to-r from-gray-900 to-gray-800 dark:from-black dark:to-gray-900 rounded-2xl p-4 sm:p-5 border-2 border-emerald-500/40 shadow-lg">
              <div class="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-emerald-500/20 border-2 border-emerald-500/40 flex items-center justify-center flex-shrink-0">
                    <Icon icon="mdi:lock-check" class="text-emerald-400 text-xl sm:text-2xl" />
                  </div>
                  <div>
                    <p class="text-[10px] font-black text-emerald-400 uppercase tracking-widest">
                      Código de Entrega
                    </p>
                    <p class="text-[9px] sm:text-[10px] text-emerald-300/70 font-bold mt-0.5">
                      Dáselo al delivery al recibir
                    </p>
                  </div>
                </div>
                <div class="flex items-center gap-2 sm:gap-3">
                  <p class="font-mono text-3xl sm:text-5xl font-black text-emerald-400 tracking-[0.25em] sm:tracking-[0.3em]"
                     style="text-shadow: 0 0 20px rgba(16, 185, 129, 0.5);">
                    {{ pedidoSeleccionado.codigoEntrega }}
                  </p>
                  <button @click="copiarCodigo(pedidoSeleccionado.codigoEntrega)"
                    class="h-10 w-10 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-emerald-400 flex items-center justify-center active:scale-95 transition flex-shrink-0">
                    <Icon icon="mdi:content-copy" class="text-lg" />
                  </button>
                </div>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
              <div class="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3">
                <p class="text-[9px] font-black text-blue-500 uppercase">Estado</p>
                <p class="text-xs font-black text-blue-700">{{ labelEstado(pedidoSeleccionado.estado) }}</p>
              </div>
              <div class="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-3">
                <p class="text-[9px] font-black text-emerald-500 uppercase">Total USD</p>
                <p class="text-sm font-black text-emerald-700">${{ Number(pedidoSeleccionado.totalUsd || 0).toFixed(2) }}</p>
              </div>
              <div class="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-3">
                <p class="text-[9px] font-black text-purple-500 uppercase">Cliente</p>
                <p class="text-xs font-black text-purple-700 truncate">{{ pedidoSeleccionado.nombreCliente }}</p>
              </div>
              <div class="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3">
                <p class="text-[9px] font-black text-amber-500 uppercase">Fecha</p>
                <p class="text-xs font-black text-amber-700">{{ formatFecha(pedidoSeleccionado.fechaPedido) }}</p>
              </div>
            </div>

            <div class="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4 space-y-3">
              <div>
                <p class="text-[10px] font-black text-gray-400 uppercase mb-2">Dirección</p>
                <p class="text-xs font-bold text-gray-800 dark:text-white">{{ pedidoSeleccionado.direccionEntrega }}</p>
                <a v-if="pedidoSeleccionado.urlMapaEntrega" :href="pedidoSeleccionado.urlMapaEntrega" target="_blank"
                  class="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-1">
                  <Icon icon="mdi:map-marker" /> Abrir en Google Maps
                </a>
              </div>
              <div v-if="pedidoSeleccionado.urlMapaEntrega && mapaEmbedUrl(pedidoSeleccionado)" class="rounded-xl overflow-hidden border-2 border-blue-200 dark:border-blue-800 shadow-sm">
                <iframe
                  :src="mapaEmbedUrl(pedidoSeleccionado)"
                  width="100%"
                  height="200"
                  style="border:0;"
                  loading="lazy"
                  referrerpolicy="no-referrer-when-downgrade"
                  allowfullscreen>
                </iframe>
              </div>
            </div>

            <div class="overflow-x-auto">
              <table class="w-full text-xs min-w-[400px]">
                <thead class="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th class="px-3 py-2 text-left font-black text-gray-400">Producto</th>
                    <th class="px-3 py-2 text-center font-black text-gray-400">Cant.</th>
                    <th class="px-3 py-2 text-center font-black text-gray-400">Kilos</th>
                    <th class="px-3 py-2 text-center font-black text-gray-400">Litros</th>
                    <th class="px-3 py-2 text-right font-black text-gray-400">Total</th>
                  </tr>
                </thead>
                <tbody class="divide-y dark:divide-gray-700">
                  <tr v-for="d in pedidoSeleccionado.detalles" :key="d.idPedidoRecargaDetalle">
                    <td class="px-3 py-2 font-bold">{{ d.nombreTipoRecarga }}</td>
                    <td class="px-3 py-2 text-center">{{ d.cantidad }}</td>
                    <td class="px-3 py-2 text-center">{{ d.kilogramos }} kg</td>
                    <td class="px-3 py-2 text-center">{{ d.litros }} L</td>
                    <td class="px-3 py-2 text-right font-black text-emerald-600">${{ Number(d.totalUsd).toFixed(2) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import {
  obtenerPedidosRecarga,
  actualizarEstadoPedido,
  asignarDeliveryAPedido,
  obtenerDeliveries,
  crearPagoPedido,
  colorEstadoPedido,
  asignarZonaAPedido,
  verificarPagoPedido,
  obtenerZonas,
} from '@/api/gas.js';

export default {
  name: 'PedidosRecargaList',
  components: { Icon },
  data() {
    return {
      pedidos: [],
      deliveries: [],
      zonas: [],
      loading: false,
      saving: false,
      searchQuery: '',
      filterEstado: '',

      paginaActual: 1,
      itemsPorPagina: 20,

      modalEstado: false,
      modalZona: false,
      modalVerificarPago: false,
      modalAsignar: false,
      modalPago: false,
      modalDetalle: false,

      pedidoSeleccionado: null,
      nuevoEstado: '',
      obsEstado: '',
      zonaSeleccionada: null,
      obsZona: '',

      deliverySeleccionado: null,
      obsAsignar: '',

      pago: { montoUsd: 0, montoBs: 0, tasaBsUsd: 0, metodoPago: 'efectivo', referencia: '' },
      pagoVerificacion: { montoUsd: 0, tasaBsUsd: 0, notas: '' },

      nuevosIds: new Set(),
      ultimaActualizacion: null,
      pollingInterval: null,
      tickInterval: null,
      sonidoActivo: true,
      menuAbierto: null,
    };
  },
  computed: {
    filteredPedidos() {
      let list = [...this.pedidos];
      if (this.filterEstado) list = list.filter(p => p.estado === this.filterEstado);
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase();
        list = list.filter(p =>
          (p.numeroPedido || '').toLowerCase().includes(q) ||
          (p.nombreCliente || '').toLowerCase().includes(q)
        );
      }
      return list;
    },

    filteredPedidosPaginados() {
      const inicio = (this.paginaActual - 1) * this.itemsPorPagina;
      return this.filteredPedidos.slice(inicio, inicio + this.itemsPorPagina);
    },

    totalPaginas() {
      return Math.max(1, Math.ceil(this.filteredPedidos.length / this.itemsPorPagina));
    },

    paginasVisibles() {
      const total = this.totalPaginas;
      const actual = this.paginaActual;
      const max = 7;

      if (total <= max) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }

      const paginas = [];
      paginas.push(1);

      let inicio = Math.max(2, actual - 2);
      let fin = Math.min(total - 1, actual + 2);

      if (actual <= 4) {
        inicio = 2;
        fin = Math.min(total - 1, 6);
      }

      if (actual >= total - 3) {
        inicio = Math.max(2, total - 5);
        fin = total - 1;
      }

      if (inicio > 2) paginas.push('...');
      for (let i = inicio; i <= fin; i++) paginas.push(i);
      if (fin < total - 1) paginas.push('...');
      paginas.push(total);

      return paginas;
    },

    deliveriesDisponibles() { return this.deliveries; },
    zonasDisponibles() { return this.zonas; },

    requiereZona() {
      if (!this.pedidoSeleccionado) return false;
      if (this.pedidoSeleccionado.estado !== 'PENDIENTE') return false;
      if (this.nuevoEstado !== 'CONFIRMADO') return false;
      const tieneZona = !!(this.pedidoSeleccionado.idZona || this.pedidoSeleccionado.nombreZona);
      return !tieneZona;
    },

    chipsEstado() {
      const estados = [
        { value: '', label: 'Todos' },
        { value: 'PENDIENTE', label: 'Pendientes' },
        { value: 'CONFIRMADO', label: 'Confirmados' },
        { value: 'PAGO_PENDIENTE', label: 'Pago pend.' },
        { value: 'PAGO_VERIFICADO', label: 'Pago OK' },
        { value: 'ASIGNADO', label: 'Asignados' },
        { value: 'EN_CAMINO', label: 'En camino' },
        { value: 'EN_PROCESO', label: 'En proceso' },
        { value: 'COMPLETADO', label: 'Completados' },
        { value: 'CANCELADO', label: 'Cancelados' },
      ];
      return estados.map(e => ({
        ...e,
        count: e.value ? this.pedidos.filter(p => p.estado === e.value).length : this.pedidos.length
      }));
    },
    completadosHoy() {
      const hoy = new Date().toDateString();
      return this.pedidos.filter(p =>
        p.estado === 'COMPLETADO' && new Date(p.fechaPedido).toDateString() === hoy
      ).length;
    },
    ingresosHoy() {
      const hoy = new Date().toDateString();
      return this.pedidos
        .filter(p => p.estado === 'COMPLETADO' && new Date(p.fechaPedido).toDateString() === hoy)
        .reduce((s, p) => s + Number(p.totalUsd || 0), 0);
    },
    haceCuanto() {
      if (!this.ultimaActualizacion) return '—';
      const seg = Math.floor((Date.now() - this.ultimaActualizacion) / 1000);
      if (seg < 60) return `hace ${seg}s`;
      if (seg < 3600) return `hace ${Math.floor(seg / 60)}min`;
      return `hace ${Math.floor(seg / 3600)}h`;
    },
    montoVerificacionValido() {
      if (!this.pedidoSeleccionado) return false;
      const monto = Number(this.pagoVerificacion.montoUsd || 0);
      const total = Number(this.pedidoSeleccionado.totalUsd || 0);
      return monto > 0 && monto <= total;
    }
  },
  watch: {
    filterEstado() { this.paginaActual = 1; },
    searchQuery() { this.paginaActual = 1; },
    totalPaginas(nuevo) {
      if (this.paginaActual > nuevo) this.paginaActual = nuevo;
    }
  },
  async mounted() {
    await this.load();
    await this.loadDeliveries();
    await this.loadZonas();
    this.iniciarPolling();
    this.tickInterval = setInterval(() => { this.$forceUpdate(); }, 5000);
  },
  beforeUnmount() {
    if (this.pollingInterval) clearInterval(this.pollingInterval);
    if (this.tickInterval) clearInterval(this.tickInterval);
  },
  methods: {
    getEstadoColor: colorEstadoPedido,

    irAPagina(n) {
      if (n < 1 || n > this.totalPaginas || n === this.paginaActual) return;
      this.paginaActual = n;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    async copiarCodigo(codigo) {
      if (!codigo) return;
      try {
        await navigator.clipboard.writeText(String(codigo));
        alert(`✅ Código ${codigo} copiado al portapapeles`);
      } catch (e) {
        try {
          const ta = document.createElement('textarea');
          ta.value = String(codigo);
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
          alert(`✅ Código ${codigo} copiado`);
        } catch (err) {
          alert(`📋 Código: ${codigo}\n\n(Cópialo manualmente)`);
        }
      }
    },

    // ⭐ Convierte el link de Google Maps en URL para iframe embed
    mapaEmbedUrl(pedido) {
      if (!pedido) return '';

      // 1) Si tiene lat/lng → embed por coordenadas (más confiable)
      if (pedido.latitudEntrega && pedido.longitudEntrega) {
        const lat = Number(pedido.latitudEntrega);
        const lng = Number(pedido.longitudEntrega);
        return `https://maps.google.com/maps?q=${lat},${lng}&z=16&output=embed`;
      }

      // 2) Si tiene URL → intenta extraer coords o query
      const url = pedido.urlMapaEntrega || '';
      if (!url) return '';

      const coordPattern = /(-?\d+\.\d+)[,\s]+(-?\d+\.\d+)/;
      const match = url.match(coordPattern);
      if (match) {
        return `https://maps.google.com/maps?q=${match[1]},${match[2]}&z=16&output=embed`;
      }

      try {
        const u = new URL(url);
        const q = u.searchParams.get('q') || u.searchParams.get('query');
        if (q) {
          return `https://maps.google.com/maps?q=${encodeURIComponent(q)}&z=16&output=embed`;
        }
      } catch (e) { /* url inválida */ }

      if (pedido.direccionEntrega) {
        return `https://maps.google.com/maps?q=${encodeURIComponent(pedido.direccionEntrega)}&z=16&output=embed`;
      }

      return '';
    },

    formatearMoneda(valor) {
      if (valor === null || valor === undefined || valor === '') return '0.00';
      const num = Number(valor);
      if (isNaN(num)) return '0.00';
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(num);
    },

    formatFecha(f) {
      if (!f) return '-';
      try {
        return new Date(f).toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: 'numeric' });
      } catch { return f; }
    },

    contador(estado) {
      return this.pedidos.filter(p => p.estado === estado).length;
    },

    labelEstado(estado) {
      const map = {
        'PENDIENTE': 'Pendiente',
        'CONFIRMADO': 'Confirmado',
        'PAGO_PENDIENTE': 'Pago Pendiente',
        'PAGO_VERIFICADO': 'Pago OK',
        'ASIGNADO': 'Asignado',
        'EN_CAMINO': 'En Camino',
        'EN_PROCESO': 'En Proceso',
        'COMPLETADO': 'Completado',
        'CANCELADO': 'Cancelado',
      };
      return map[estado] || estado;
    },

    labelPago(p) {
      if (p.estadoPago === 'pagado') return 'Pagado';
      if (p.estadoPago === 'verificado') return 'Verificado';
      if (p.estadoPago === 'parcial') return 'Parcial';
      if (p.estado === 'PAGO_PENDIENTE') return 'Por verificar';
      return 'Sin pago';
    },

    puedeAsignarZona(p) {
      return ['PENDIENTE', 'CONFIRMADO', 'PAGO_PENDIENTE', 'PAGO_VERIFICADO'].includes(p.estado);
    },
    puedeRegistrarPago(p) {
      return ['CONFIRMADO', 'PAGO_PENDIENTE', 'PAGO_VERIFICADO'].includes(p.estado);
    },
    puedeVerificarPago(p) {
      return p.estado === 'PAGO_PENDIENTE';
    },
    puedeAsignarDelivery(p) {
      const pagoOk = p.estadoPago === 'verificado' || p.estadoPago === 'pagado' || p.estado === 'PAGO_VERIFICADO';
      const tieneZona = !!(p.idZona || p.nombreZona);
      const estadoOk = ['CONFIRMADO', 'PAGO_PENDIENTE', 'PAGO_VERIFICADO', 'ASIGNADO'].includes(p.estado);
      return pagoOk && tieneZona && estadoOk;
    },

    accionPrincipal(p) {
      switch (p.estado) {
        case 'PENDIENTE': {
          const tieneZona = !!(p.idZona || p.nombreZona);
          if (!tieneZona) {
            return {
              icon: 'mdi:map-marker-check',
              label: 'Confirmar y Zona',
              color: 'blue',
              handler: () => this.abrirCambiarEstado(p)
            };
          }
          return {
            icon: 'mdi:clock-outline',
            label: 'Esperando Pago',
            color: 'gray',
            handler: () => this.abrirDetalle(p)
          };
        }
        case 'CONFIRMADO':
          return {
            icon: 'mdi:clock-outline',
            label: 'Esperando Pago',
            color: 'gray',
            handler: () => this.abrirDetalle(p)
          };
        case 'PAGO_PENDIENTE':
          return {
            icon: 'mdi:check-decagram',
            label: 'Verificar Pago',
            color: 'teal',
            handler: () => this.abrirVerificarPago(p)
          };
        case 'PAGO_VERIFICADO':
          return {
            icon: 'mdi:moped',
            label: 'Asignar Delivery',
            color: 'purple',
            handler: () => this.abrirAsignarDelivery(p)
          };
        case 'ASIGNADO':
          return {
            icon: 'mdi:map-marker-path',
            label: 'En Camino',
            color: 'amber',
            handler: () => this.abrirCambiarEstado(p)
          };
        case 'EN_CAMINO':
          return {
            icon: 'mdi:fire',
            label: 'En Proceso',
            color: 'orange',
            handler: () => this.abrirCambiarEstado(p)
          };
        case 'EN_PROCESO':
          return {
            icon: 'mdi:check-all',
            label: 'Completar',
            color: 'green',
            handler: () => this.abrirCambiarEstado(p)
          };
        default:
          return {
            icon: 'mdi:eye',
            label: 'Ver',
            color: 'gray',
            handler: () => this.abrirDetalle(p)
          };
      }
    },

    colorBtn(c) {
      const map = {
        blue: 'bg-blue-600 hover:bg-blue-700',
        purple: 'bg-purple-600 hover:bg-purple-700',
        amber: 'bg-amber-500 hover:bg-amber-600',
        orange: 'bg-orange-500 hover:bg-orange-600',
        green: 'bg-green-600 hover:bg-green-700',
        emerald: 'bg-emerald-600 hover:bg-emerald-700',
        teal: 'bg-teal-600 hover:bg-teal-700',
        gray: 'bg-gray-500 hover:bg-gray-600',
      };
      return map[c] || map.blue;
    },

    toggleMenuAcciones(id) {
      this.menuAbierto = this.menuAbierto === id ? null : id;
    },
    ejecutarAccion(p, tipo) {
      this.menuAbierto = null;
      if (tipo === 'copiarCodigo') this.copiarCodigo(p.codigoEntrega);
      else if (tipo === 'zona') this.abrirAsignarZona(p);
      else if (tipo === 'estado') this.abrirCambiarEstado(p);
      else if (tipo === 'delivery') this.abrirAsignarDelivery(p);
      else if (tipo === 'pago') this.abrirPago(p);
      else if (tipo === 'verificarPago') this.abrirVerificarPago(p);
      else if (tipo === 'detalle') this.abrirDetalle(p);
      else if (tipo === 'imprimirTicket') this.imprimirTicketPedido(p);
    },

    async load() {
      this.loading = true;
      try {
        const res = await obtenerPedidosRecarga();
        if (res.success) {
          this.pedidos = res.data;
          this.ultimaActualizacion = Date.now();
        }
      } finally {
        this.loading = false;
      }
    },

    async loadDeliveries() {
      const res = await obtenerDeliveries('disponibles');
      if (res.success) this.deliveries = res.data;
    },

    async loadZonas() {
      try {
        const res = await obtenerZonas();
        if (res.success) this.zonas = res.data;
      } catch (e) {
        console.warn('No se pudieron cargar zonas');
      }
    },

    iniciarPolling() {
      this.pollingInterval = setInterval(async () => {
        if (this.saving || this.modalEstado || this.modalAsignar || this.modalPago
            || this.modalDetalle || this.modalZona || this.modalVerificarPago) return;
        await this.refreshSilencioso();
      }, 15000);
    },

    async refreshSilencioso() {
      try {
        const res = await obtenerPedidosRecarga();
        if (!res.success) return;

        const idsAnteriores = new Set(this.pedidos.map(p => p.idPedidoRecarga));
        const nuevos = res.data.filter(p => !idsAnteriores.has(p.idPedidoRecarga));

        if (nuevos.length > 0) {
          nuevos.forEach(p => this.nuevosIds.add(p.idPedidoRecarga));
          this.notificarNuevos(nuevos.length);
          this.reproducirSonido();
        }

        this.pedidos = res.data;
        this.ultimaActualizacion = Date.now();
      } catch (e) { /* silencioso */ }
    },

    notificarNuevos(cantidad) {
      if (!('Notification' in window)) return;
      if (Notification.permission === 'granted') {
        new Notification('🛒 Nuevo pedido de recarga', {
          body: `Han llegado ${cantidad} pedido(s) nuevo(s)`,
        });
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission();
      }
    },

    reproducirSonido() {
      if (!this.sonidoActivo) return;
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(880, ctx.currentTime);
        osc.frequency.setValueAtTime(660, ctx.currentTime + 0.15);
        gain.gain.setValueAtTime(0.15, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.4);
      } catch (e) { /* silencioso */ }
    },

    async imprimirTicketPedido(pedido) {
      try {
        const { imprimirTicketPedido } = await import('@/utils/ticketGasGenerator');
        imprimirTicketPedido(pedido);
      } catch (err) {
        console.error('❌ Error imprimiendo ticket:', err);
      }
    },

    abrirDetalle(p) {
      this.nuevosIds.delete(p.idPedidoRecarga);
      this.pedidoSeleccionado = p;
      this.modalDetalle = true;
    },

    abrirCambiarEstado(p) {
      this.nuevosIds.delete(p.idPedidoRecarga);
      this.pedidoSeleccionado = p;
      this.nuevoEstado = p.estado === 'PENDIENTE' ? 'CONFIRMADO' : p.estado;
      this.obsEstado = '';
      this.zonaSeleccionada = p.idZona || null;
      this.modalEstado = true;
    },

    cerrarModalEstado() {
      this.modalEstado = false;
      this.zonaSeleccionada = null;
    },

    abrirAsignarZona(p) {
      this.nuevosIds.delete(p.idPedidoRecarga);
      this.pedidoSeleccionado = p;
      this.zonaSeleccionada = p.idZona || null;
      this.obsZona = '';
      this.modalZona = true;
    },

    abrirVerificarPago(p) {
      this.nuevosIds.delete(p.idPedidoRecarga);
      this.pedidoSeleccionado = p;

      const montoSugerido = Number(p.montoReportadoUsd) > 0
        ? Number(p.montoReportadoUsd)
        : Number(p.totalUsd) || 0;

      this.pagoVerificacion = {
        montoUsd: montoSugerido,
        tasaBsUsd: Number(p.tasaReportadaBsUsd || p.tasaBsUsd || 0),
        notas: ''
      };
      this.modalVerificarPago = true;
    },

    abrirAsignarDelivery(p) {
      this.nuevosIds.delete(p.idPedidoRecarga);
      this.pedidoSeleccionado = p;

      if (!this.puedeAsignarDelivery(p)) {
        const motivo = !p.nombreZona
          ? 'Este pedido no tiene zona asignada. Asígnala primero.'
          : 'El pago aún no está verificado. Verifícalo primero.';
        alert('⚠ ' + motivo);
        return;
      }

      this.deliverySeleccionado = null;
      this.obsAsignar = '';
      this.modalAsignar = true;
    },

    abrirPago(p) {
      this.nuevosIds.delete(p.idPedidoRecarga);
      this.pedidoSeleccionado = p;
      this.pago = {
        montoUsd: Number(p.saldoPendienteUsd || 0),
        montoBs: Number(p.saldoPendienteBs || 0),
        tasaBsUsd: Number(p.tasaBsUsd || 0),
        metodoPago: 'efectivo',
        referencia: ''
      };
      this.modalPago = true;
    },

    async cambiarEstado() {
      if (this.requiereZona && !this.zonaSeleccionada) {
        alert('⚠ Debes seleccionar una zona para confirmar el pedido.');
        return;
      }

      this.saving = true;
      try {
        const zonaActual = this.pedidoSeleccionado.idZona;
        if (this.requiereZona || (this.zonaSeleccionada && this.zonaSeleccionada !== zonaActual)) {
          const resZona = await asignarZonaAPedido(this.pedidoSeleccionado.idPedidoRecarga, {
            idZona: this.zonaSeleccionada,
            observaciones: this.obsEstado
          });
          if (!resZona.success) {
            alert(resZona.error || 'Error asignando zona');
            return;
          }
        }

        const res = await actualizarEstadoPedido(this.pedidoSeleccionado.idPedidoRecarga, {
          nuevoEstado: this.nuevoEstado,
          observaciones: this.obsEstado
        });

        if (res.success) {
          await this.load();
          this.cerrarModalEstado();
        } else {
          alert(res.error || 'Error');
        }
      } finally {
        this.saving = false;
      }
    },

    async asignarZona() {
      if (!this.zonaSeleccionada) {
        alert('Selecciona una zona');
        return;
      }
      this.saving = true;
      try {
        const res = await asignarZonaAPedido(this.pedidoSeleccionado.idPedidoRecarga, {
          idZona: this.zonaSeleccionada,
          observaciones: this.obsZona
        });
        if (res.success) {
          await this.load();
          this.modalZona = false;
        } else {
          alert(res.error || 'Error');
        }
      } finally {
        this.saving = false;
      }
    },

    async verificarPago() {
      const monto = Number(this.pagoVerificacion.montoUsd || 0);
      const total = Number(this.pedidoSeleccionado.totalUsd || 0);

      if (monto <= 0) { alert('⚠ El monto debe ser mayor a 0'); return; }
      if (monto > total) {
        alert(`⚠ El monto ($${monto.toFixed(2)}) no puede superar el total ($${total.toFixed(2)})`);
        return;
      }

      this.saving = true;
      try {
        const res = await verificarPagoPedido(this.pedidoSeleccionado.idPedidoRecarga, {
          montoUsd: monto,
          tasaBsUsd: this.pagoVerificacion.tasaBsUsd,
          notas: this.pagoVerificacion.notas,
          accion: 'verificar'
        });
        if (res.success) {
          const pedidoParaTicket = { ...this.pedidoSeleccionado };
          await this.load();
          this.modalVerificarPago = false;
          this.imprimirTicketPedido(pedidoParaTicket);
          setTimeout(() => {
            alert('✅ Pago verificado. Ya puedes asignar delivery.');
          }, 100);
        } else {
          alert(res.error || 'Error');
        }
      } finally {
        this.saving = false;
      }
    },

    async rechazarPago() {
      if (!confirm('¿Rechazar el pago reportado por el cliente?')) return;
      this.saving = true;
      try {
        const res = await verificarPagoPedido(this.pedidoSeleccionado.idPedidoRecarga, {
          montoUsd: this.pagoVerificacion.montoUsd,
          notas: this.pagoVerificacion.notas,
          accion: 'rechazar'
        });
        if (res.success) {
          await this.load();
          this.modalVerificarPago = false;
          alert('Pago rechazado.');
        } else {
          alert(res.error || 'Error');
        }
      } finally {
        this.saving = false;
      }
    },

    async asignarDelivery() {
      this.saving = true;
      try {
        const res = await asignarDeliveryAPedido(this.pedidoSeleccionado.idPedidoRecarga, {
          idDelivery: this.deliverySeleccionado,
          observaciones: this.obsAsignar
        });
        if (res.success) {
          await this.load();
          this.modalAsignar = false;
        } else {
          alert(res.error || 'Error');
        }
      } finally {
        this.saving = false;
      }
    },

    async registrarPago() {
      this.saving = true;
      try {
        const res = await crearPagoPedido({
          idPedidoRecarga: this.pedidoSeleccionado.idPedidoRecarga,
          ...this.pago
        });
        if (res.success) {
          await this.load();
          this.modalPago = false;
          alert('Pago registrado');
        } else {
          alert(res.error || 'Error');
        }
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }

@keyframes pulse-slow {
  0%, 100% { background-color: rgba(251, 191, 36, 0.08); }
  50% { background-color: rgba(251, 191, 36, 0.22); }
}
.animate-pulse-slow {
  animation: pulse-slow 2s ease-in-out infinite;
}
</style>